import * as React from 'react';
import styled from 'styled-components';

import {
  getDailyInspectionReport,
  ReportTableType,
} from '../../api/reports';

import { DATETIME_FORMAT } from '../../constants';

import Table from '../../organisms/Table';

import { formatDateTime } from '../../utils/formatDate';
import tableColumnsFormatter from '../../utils/tableColumnsFormatter';
import Wrapper from './print-mixin';

const DailyInspectionReport = React.forwardRef(
  (
    {
      fromDate,
      toDate,
      inspector,
      setIsValidForExport,
    }: ReportTableType,
    ref,
  ) => {
    const [
      isNullInspections,
      setIsNullInspections,
    ] = React.useState(false);
    const [
      inspectorName,
      setInspectorName,
    ] = React.useState('');
    const [lastComment, setLastComment] = React.useState(
      null,
    );
    const [inspectionCount, setInspectionCount] = React.useState(null);

    const data = {
      fromDate,
      toDate,
      inspectors: inspector.map(i => i.id),
    };

    const getModifiedDailyInspectionReport = () =>
      Promise.all([getDailyInspectionReport(data)])
        .then(([reports]) => {
          if (reports.data?.length === 0) {
            setIsNullInspections(true);
            setIsValidForExport(false);
          } else {
            setIsNullInspections(false);
            setIsValidForExport(true);
            setInspectionCount(reports.data?.count);
            const commentData = reports.data?.data.map(el => {
              return {
                id: el.id,
                lastComment: el.lastComment,
              };
            });

            const informationData = reports.data?.data.map(
              el => ({
                id: el?.id,
                information: el?.information,
              }),
            );

            const recordData = reports.data?.data.map(
              el => ({
                id: el.id,
                name: `${el?.projectName} | ${el?.permitName}`,
                address: el?.projectAddress,
              }),
            );

            setLastComment({
              commentData,
              informationData,
              recordData,
              isDailyInspection: true,
            });
          }
          return {
            ...reports,
            data: {
              ...reports.data?.data,
              results: reports.data?.data.map(el => {
                delete (el.projectAddress);
                delete (el.inspectionDate);
                return {
                  ...el,
                  inspector: el.inspectorName,
                  // EA-1408: AV Inspector column. Backend returns avoperator_name
                  // (camelCased to avoperatorName by the response interceptor),
                  // which is '' when no AV inspector is assigned.
                  avInspector: el.avoperatorName || '',
                  period: el.period == null || el.period === 2 ? null : el.period === 1 ? 'PM' : 'AM',
                  // projectNamePermitName: `${el.projectName} | ${el.permitName}`,
                };
              }),
            },
          };
        })
        .catch(err => console.log(err))
        .finally(() => {});

    const columns = column => {
      if (
        [
          'franchiseName',
          'inspectorName',
          // EA-1408: raw AV field is re-keyed to `avInspector` below; hide it.
          'avoperatorName',
          'lastComment',
          'projectName',
          'permitName',
          'information',
        ].includes(column)
      ) {
        return null;
      }

      switch (column) {
        case 'contactDetails':
          return 'Contact Details';
        case 'contactName':
          return 'Contact';
        case 'id':
          return 'Inspection ID';
        case 'inspectionDate':
          return 'Inspection Date';
        case 'inspectionTypeName':
          return 'Inspection Type';
        case 'jurisdiction':
          return 'Jurisdiction';
        case 'permitNumber':
          return 'Permit Number';
        case 'projectAddress':
          return 'Project Address';
        case 'projectManager':
          return 'Project Manager';
        case 'period':
          return 'AM/PM';
        case 'projectNamePermitName':
          return 'Project Name | Permit Name';
        case 'inspector':
          return 'Inspector';
        case 'avInspector':
          return 'AV Inspector';
        default:
          return tableColumnsFormatter(column);
      }
    };

    const columnsOrder = [
      'projectNamePermitName',
      'permitNumber',
      'projectAddress',
      'inspector',
      // EA-1408: AV Inspector sits next to the regular Inspector.
      'avInspector',
      'id',
      'inspectionTypeName',
      'projectManager',
      'jurisdiction',
      'inspectionDate',
      'period',
      'contactName',
      'contactDetails',
    ];

    if (isNullInspections) {
      return (
        <InfoText>
          No project was found with selected date or Inspector.
        </InfoText>
      );
    }

    return (
      <Wrapper ref={ref}>
        <Title>Daily Inspection Report</Title>

        <List>
          <li>
            <Bold>Inspectors: </Bold>   {inspector && inspector.length
                    ? inspector.map(i => `${i.firstName} ${i.lastName}`).join(", ")
                    : "None selected"}
          </li>
          <li>
            <Bold>Inspection Date: </Bold>
            {formatDateTime(fromDate, "MM/dd/yyy")}
          </li>
          <li>
            <Bold>Report Date: </Bold>
            {formatDateTime(new Date(), DATETIME_FORMAT)}
          </li>
          <li>
            <Bold>Total Inspections: </Bold>{inspectionCount}
          </li>
        </List>

        <Table
          customSearch
          columnsOrder={columnsOrder}
          columns={columns}
          rows={getModifiedDailyInspectionReport}
          onEdit={null}
          onDelete={null}
          onConfigure={null}
          additionalActions={null}
          additionalRow={lastComment}
          smallPrintHeader
        />
      </Wrapper>
    );
  },
);

const FONT_STACK = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 16px;
  font-family: ${FONT_STACK};
  li {
    padding: 6px 0;
    font-size: 0.9375rem;
    color: var(--mainText);
  }
`;

const Bold = styled.span`
  color: var(--mainText);
  font-weight: 700;
`;
const Title = styled.h1`
  margin: 0 0 16px;
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--mainText);
  font-family: ${FONT_STACK};
`;

const InfoText = styled.div`
  margin: 0 auto;
  padding: 24px;
  font-size: 1rem;
  color: var(--neutral-500);
  text-align: center;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  font-family: ${FONT_STACK};
`;

export default DailyInspectionReport;
